import { HttpResponse } from '@angular/common/http';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import moment from 'moment';
import { JobProcedureFormPhoto } from 'src/app/models/procedure-copilot-confirmation-details.model';
import { DocumentsService } from 'src/app/services/sections/documents/documents.service';
import { GlobalUtilsService } from 'src/app/services/ui/global-utils.service';
import { DocumentViewerComponent, DocViewerDocumentDetails } from '../../document-viewer/document-viewer.component';
import { GenericModalComponent } from '../../generic-modal/generic-modal.component';

@Component({
  selector: 'step-photos',
  templateUrl: './step-photos.component.html',
  styleUrls: ['./step-photos.component.scss']
})
export class StepPhotosComponent implements OnInit {
  @Input() isLoading: boolean = false;
  @Input() hideLoaded: boolean = false;
  @Input() photos: JobProcedureFormPhoto[] = [];

  @ViewChild('photoModal', { static: true }) public photoModal: GenericModalComponent;
  @ViewChild('photoPreviewViewer') private photoPreviewViewer?: DocumentViewerComponent;

  public readonly self = this;
  private dateFormat = 'M/D/YYYY';
  public isPhotoLoading: boolean = false;
  public documentDetails: DocViewerDocumentDetails;
  public onPhotoModalHide = () => this.handlePhotoModalHide();
  public docViewerHeaderReady: boolean = false;
  public customButtons: any[] = [];
  public downloadButton: HTMLButtonElement;
  public disableDownloadDocButton: boolean;

  private photoViewerUiInstance: any;
  private photoViewerDocumentLoadedHandler = () => {
    this.guardPhotoViewerTextRequests();
    this.ensureMobileFitMode();
    this.applyMobilePhotoPreviewUiOverrides();
  };
  private photoViewerPatchedDocument: any;
  private photoViewerOriginalTextLoader: ((...args: any[]) => any) | null = null;

  constructor(private _sanitizer: DomSanitizer,
    private documentsService: DocumentsService) { }

  ngOnInit(): void {
  }

  public imageThumbnail(photo: JobProcedureFormPhoto): SafeResourceUrl | string {
    return !!photo.imageThumbnail ? this._sanitizer.bypassSecurityTrustResourceUrl(`data:image/jpeg;base64, ${photo.imageThumbnail}`) : "../../../assets/icons/no_photo.svg";
  }

  formatDate(date: string | Date | undefined): string {
    return !!date
      ? moment(date).format(this.dateFormat)
      : '';
  }

  public loadDocument(photo: JobProcedureFormPhoto): void {
    this.photoModal.open();
    this.isPhotoLoading = true;
    this.documentsService.getDocumentByOid(photo.documentOid, photo.documentId, false)
      .subscribe((response: HttpResponse<Blob>) => {       
        this.documentDetails = {
          documentId: photo.documentId,
          content: response.body,
          fileName: `${photo.measurementName} - ${photo.name}`,
          documentDisplayName: `${photo.measurementName} - ${photo.name}`,
          documentDownloadName: `${photo.measurementName} - ${photo.name}`,
          isFitToPage: true
        };
        this.isPhotoLoading = false;
      });
  }

  public isPhotoModalOpen(): boolean {
    return !!this.photoModal?.isOpen;
  }

  public closePhotoModal(): void {
    if (this.photoModal?.isOpen) {
      this.photoModal.hide();
    }
  }


  public setHeaderElements(header) {
    this.docViewerHeaderReady = false;
    const items: any[] = header.getItems();
    const toolbarItems = [...this.customButtons, ...items];
    header.update(toolbarItems);
    this.docViewerHeaderReady = true;
  }

  public afterWebViewerRender(instance): void {
    instance.setHeaderItems((header) => {
      this.setHeaderElements(header);
    });

    this.detachPhotoViewerListener();
    this.photoViewerUiInstance = instance;
    if (this.photoViewerUiInstance?.addEventListener) {
      this.photoViewerUiInstance.addEventListener('documentLoaded', this.photoViewerDocumentLoadedHandler);
    }

    if (instance?.closeElements) {
      instance.closeElements(['leftPanel', 'thumbnailsPanel']);
    }

    if (instance?.disableElements) {
      instance.disableElements(['leftPanelButton', 'thumbnailsPanelButton']);
    }

    this.guardPhotoViewerTextRequests();
    this.ensureMobileFitMode();
    this.applyMobilePhotoPreviewUiOverrides();
  }

  private handlePhotoModalHide(): void {
    this.removeMobilePhotoPreviewStyleOverrides();
    this.detachPhotoViewerListener();
    this.photoViewerUiInstance = null;
    this.releasePhotoViewerTextGuard();
  }

  private detachPhotoViewerListener(): void {
    if (this.photoViewerUiInstance?.removeEventListener) {
      this.photoViewerUiInstance.removeEventListener('documentLoaded', this.photoViewerDocumentLoadedHandler);
    }
  }

  private isPhotoPreviewMobileMode(): boolean {
    if (GlobalUtilsService.isMobileDevice) {
      return true;
    }

    try {
      return window.matchMedia && window.matchMedia('(max-width: 600px)').matches;
    } catch {
      return false;
    }
  }

  private isPhotoPreviewImage(): boolean {
    const candidateName = (this.documentDetails?.fileName || this.documentDetails?.documentDisplayName || '').toLowerCase();
    return /\.(png|jpe?g|gif|bmp|webp)$/.test(candidateName);
  }

  private ensureMobileFitMode(): void {
    if (!this.isPhotoPreviewMobileMode()) {
      return;
    }

    const viewer = this.photoViewerUiInstance;
    const fitWidthMode = viewer?.FitMode?.FitWidth;
    const fitPageMode = viewer?.FitMode?.FitPage;
    const shouldFitPage = this.isPhotoPreviewImage() && this.isPortraitPhotoPreview();

    try {
      if (viewer?.setFitMode && fitWidthMode) {
        if (shouldFitPage && fitPageMode) {
          viewer.setFitMode(fitPageMode);
        } else {
          viewer.setFitMode(fitWidthMode);
        }
      } else if (viewer?.UI?.setFitMode && viewer?.UI?.FitMode?.FitWidth) {
        if (shouldFitPage && viewer.UI.FitMode.FitPage) {
          viewer.UI.setFitMode(viewer.UI.FitMode.FitPage);
        } else {
          viewer.UI.setFitMode(viewer.UI.FitMode.FitWidth);
        }
      }
    } catch {
    }
  }

  private isPortraitPhotoPreview(): boolean {
    const docViewer = this.getPhotoDocViewer();
    const doc = docViewer?.getDocument && docViewer.getDocument();
    if (!doc?.getPageInfo) {
      return false;
    }

    try {
      const pageInfo = doc.getPageInfo(1);
      const pageWidth = pageInfo?.width;
      const pageHeight = pageInfo?.height;
      if (!pageWidth || !pageHeight) {
        return false;
      }
      return pageHeight > pageWidth;
    } catch {
      return false;
    }
  }

  private applyMobilePhotoPreviewUiOverrides(): void {
    if (!this.isPhotoPreviewImage() || !this.isPhotoPreviewMobileMode()) {
      this.removeMobilePhotoPreviewStyleOverrides();
      return;
    }

    const viewer = this.photoViewerUiInstance;
    if (!viewer?.disableElements) {
      return;
    }

    try {
      viewer.disableElements([
        'header',
        'toolsHeader',
        'textSelectButton',
        'selectToolButton',
        'panToolButton'
      ]);
    } catch {
    }

    this.applyMobilePhotoPreviewStyleOverrides();
  }

  private applyMobilePhotoPreviewStyleOverrides(): void {
    const iframeDocument = this.photoViewerUiInstance?.iframeWindow?.document;
    if (!iframeDocument) {
      return;
    }

    const styleId = 'photoPreviewMobileOverrides';
    if (iframeDocument.getElementById(styleId)) {
      return;
    }

    const style = iframeDocument.createElement('style');
    style.id = styleId;
    style.textContent = `
      [data-element="documentContainer"],
      .DocumentContainer,
      .PageContainer,
      .pageContainer,
      .page-container {
        border: 0 !important;
        box-shadow: none !important;
        outline: 0 !important;
      }

      [data-element="documentContainer"] .page,
      [data-element="documentContainer"] canvas,
      .DocumentContainer .page,
      .DocumentContainer canvas,
      .PageContainer .page,
      .PageContainer canvas {
        border: 0 !important;
        box-shadow: none !important;
        outline: 0 !important;
      }
    `;

    (iframeDocument.head || iframeDocument.documentElement).appendChild(style);
  }

  private removeMobilePhotoPreviewStyleOverrides(): void {
    const iframeDocument = this.photoViewerUiInstance?.iframeWindow?.document;
    const styleElement = iframeDocument?.getElementById('photoPreviewMobileOverrides');
    if (styleElement?.parentNode) {
      styleElement.parentNode.removeChild(styleElement);
    }
  }

  private getPhotoDocViewer(): any {
    const viewerComponentRef = this.photoPreviewViewer as any;
    const viewerInstance = viewerComponentRef?.wvInstance || null;
    const docViewerFromInstance = viewerInstance?.Core?.documentViewer || viewerInstance?.docViewer || null;
    const docViewerFromUi = this.photoViewerUiInstance?.docViewer || this.photoViewerUiInstance?.Core?.documentViewer || null;
    return docViewerFromInstance || docViewerFromUi || null;
  }

  /*
   WebViewer (PDFTron) v.10.7.3 can request text data for image documents, but the returned
   internal text payload is missing those functions, triggering "TypeError: Fa.tf is not a function" in the console, guardPhotoViewerTextRequests().
   */
  private guardPhotoViewerTextRequests(): void {
    if (!this.isPhotoPreviewImage()) {
      this.releasePhotoViewerTextGuard();
      return;
    }

    const docViewer = this.getPhotoDocViewer();
    const doc = docViewer?.getDocument && docViewer.getDocument();
    if (!doc || doc === this.photoViewerPatchedDocument) {
      return;
    }

    this.releasePhotoViewerTextGuard();

    const originalLoader = doc.Uh;
    if (typeof originalLoader !== 'function') {
      return;
    }

    doc.Uh = (pageIndex: number, callback?: (textData?: any) => void) => {
      const safeCallback = (textData?: any) => {
        if (!textData || typeof textData.tf !== 'function' || typeof textData.Kc !== 'string') {
          callback?.(this.createEmptyTextData());
          return;
        }
        callback?.(textData);
      };

      try {
        return originalLoader.call(doc, pageIndex, safeCallback);
      } catch {
        safeCallback();
        return null;
      }
    };

    this.photoViewerPatchedDocument = doc;
    this.photoViewerOriginalTextLoader = originalLoader;
  }

  private releasePhotoViewerTextGuard(): void {
    if (this.photoViewerPatchedDocument && this.photoViewerOriginalTextLoader) {
      this.photoViewerPatchedDocument.Uh = this.photoViewerOriginalTextLoader;
    }
    this.photoViewerPatchedDocument = null;
    this.photoViewerOriginalTextLoader = null;
  }

  private createEmptyTextData(): any {
    return {
      Kc: '',
      Hf: [],
      tf: () => [],
      ready: () => true
    };
  }
}
